import { getMessagesFromRequest } from 'lib/api/chat';
import { ConfirmingRequest } from 'lib/types/models';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import { BaseExportDialog } from '../../../common/SimulationExportDialog/components/BaseExportDialog';
import {
  ChatMessage,
  ChatMessagesDialog,
} from '../../../common/SimulationExportDialog/components/ChatMessagesDialog';
import { ConfirmingCheckboxes } from '../../../common/SimulationExportDialog/components/ConfirmingCheckboxes';
import { ConfirmingCheckboxesState } from '../../../common/SimulationExportDialog/hooks/useConfirmingCheckboxes';
import { useConfirmingCheckboxes } from '../../../common/SimulationExportDialog/hooks/useConfirmingCheckboxes';
import { ConfirmingExportRequest } from '../../../common/SimulationExportDialog/types/ConfirmingExportRequest';
import { buildConfirmingRequest } from '../../../common/SimulationExportDialog/utils/buildConfirmingRequest';
import { handleExportConfirmingSimulation } from '../../../common/SimulationExportDialog/utils/exportConfirmingSimulation';
import { buildRequestId } from '../../../common/utils';

interface ExportDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  selectedRequest: ConfirmingRequest;
}

const ExportDialog = (props: ExportDialogProps) => {
  const { open, setOpen, selectedRequest } = props;
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  const [checkboxes, setters] = useConfirmingCheckboxes();

  const customBuildRequest = (
    selectedRequest: unknown,
    checkboxes: unknown,
  ): unknown => {
    return buildConfirmingRequest({
      selectedRequest: selectedRequest as ConfirmingRequest,
      checkboxes: checkboxes as ConfirmingCheckboxesState,
    });
  };

  const customOnExport = async (request: unknown): Promise<void> => {
    if (!user) return;
    await handleExportConfirmingSimulation({
      dispatch,
      user,
      request: request as ConfirmingExportRequest,
      checkboxes,
    });
  };

  // Handlers para el chat
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [loadingChatMessages, setLoadingChatMessages] = useState(false);

  const handleChatButtonClick = () => {
    setChatDialogOpen(true);
  };

  const handleChatAsObservationsChange = async (checked: boolean) => {
    setters.setShowChatAsObservations(checked);

    if (checked) {
      const hasExistingSelection =
        checkboxes.selectedChatMessages?.length > 0 &&
        checkboxes.selectedChatMessagesObjects?.length > 0;

      if (!hasExistingSelection) {
        setLoadingChatMessages(true);
        try {
          const requestId = buildRequestId(
            selectedRequest,
            'confirming-request',
          );
          const response = await getMessagesFromRequest(dispatch, {
            requestType: 'confirming',
            requestId: Number(requestId),
          });

          if (response?.messages && response.messages.length > 0) {
            const mappedMessages: ChatMessage[] = response.messages.map(
              (
                msg: {
                  id?: string;
                  userId?: number;
                  text?: string;
                  userName?: string;
                  userRole?: string;
                  timestamp?: string | number | Date;
                },
                index: number,
              ) => ({
                id: msg.id || `msg-${index}-${msg.timestamp}`,
                userId: msg.userId || 0,
                text: msg.text || '',
                userName: msg.userName || 'Usuario',
                userRole: msg.userRole || '',
                timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
              }),
            );

            const allIds = mappedMessages.map((msg) => msg.id);
            setters.setSelectedChatMessages(allIds);
            setters.setSelectedChatMessagesObjects(mappedMessages);
          } else {
            setters.setSelectedChatMessages([]);
            setters.setSelectedChatMessagesObjects([]);
          }
        } catch (error) {
          console.error('Error loading chat messages:', error);
          setters.setSelectedChatMessages([]);
          setters.setSelectedChatMessagesObjects([]);
        } finally {
          setLoadingChatMessages(false);
        }
      }
    } else {
      setters.setSelectedChatMessages([]);
      setters.setSelectedChatMessagesObjects([]);
      setLoadingChatMessages(false);
    }
  };

  const handleChatSelectionChange = (selectedMessages: ChatMessage[]) => {
    const selectedIds = selectedMessages.map((msg) => msg.id);
    setters.setSelectedChatMessages(selectedIds);
    setters.setSelectedChatMessagesObjects(selectedMessages);
  };

  return (
    <>
      <BaseExportDialog
        open={open}
        setOpen={setOpen}
        selectedRequest={selectedRequest}
        checkboxes={checkboxes}
        setters={setters}
        customCheckboxes={
          <ConfirmingCheckboxes
            checkboxes={checkboxes}
            setters={setters}
            onChatButtonClick={handleChatButtonClick}
            onChatAsObservationsChange={(checked) => {
              void handleChatAsObservationsChange(checked);
            }}
          />
        }
        customBuildRequest={customBuildRequest}
        customOnExport={customOnExport}
        dialogMaxWidth="md"
        dialogFullWidth={true}
        chatRequestType="confirming-request"
        externalLoadingChatMessages={loadingChatMessages}
      />
      {checkboxes.selectedChatMessages && (
        <ChatMessagesDialog
          open={chatDialogOpen}
          onClose={() => setChatDialogOpen(false)}
          selectedRequest={selectedRequest}
          requestType="confirming"
          selectedMessages={checkboxes.selectedChatMessages}
          onSelectionChange={handleChatSelectionChange}
        />
      )}
    </>
  );
};

export default ExportDialog;
